import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { customerApi, money, formatApiError } from "@/lib/api";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Package, LogOut, MailCheck, MailWarning, ClipboardList, MessageSquare, User, CreditCard, Truck } from "lucide-react";
import { toast } from "sonner";

const emptyAddr = { line1: "", line2: "", city: "", region: "", postcode: "", country: "New Zealand" };
const fmtNZ = (v) => v ? new Date(v).toLocaleString("en-NZ", { timeZone: "Pacific/Auckland", dateStyle: "medium", timeStyle: "short" }) : "";

export default function AccountPage() {
  const { customer, logout, refresh } = useCustomerAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [tab, setTab] = useState("orders");
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (customer === false) navigate("/login"); }, [customer, navigate]);
  useEffect(() => {
    if (customer && typeof customer === "object") {
      setProfile({
        name: customer.name || "", phone: customer.phone || "", company: customer.company || "",
        saved_address: { ...emptyAddr, ...(customer.saved_address || {}) },
      });
      customerApi.get("/customer/orders").then((r) => setOrders(r.data)).catch(() => {});
      customerApi.get("/customer/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
    }
  }, [customer]);

  if (!customer || typeof customer !== "object" || !profile)
    return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;

  const setAddr = (k, v) => setProfile((p) => ({ ...p, saved_address: { ...p.saved_address, [k]: v } }));

  const saveProfile = async () => {
    setSaving(true);
    try {
      await customerApi.put("/customer/profile", profile);
      await refresh();
      toast.success("Details saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const resendVerify = async () => {
    try {
      await customerApi.post("/customer/resend-verification", { base_url: window.location.origin });
      toast.success("Verification email sent");
    } catch { toast.error("Could not send verification email"); }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-extrabold" data-testid="account-heading">My Account</h1>
            <p className="text-muted-foreground">{customer.email}</p>
          </div>
          <div className="flex items-center gap-3">
            {customer.email_verified
              ? <Badge className="bg-emerald-600 hover:bg-emerald-600 gap-1"><MailCheck size={13} /> Verified</Badge>
              : <button data-testid="resend-verification" onClick={resendVerify} className="inline-flex items-center gap-1 text-sm text-amber-600 hover:underline"><MailWarning size={15} /> Verify email</button>}
            <Button variant="outline" data-testid="account-logout" onClick={() => { logout(); navigate("/"); }} className="gap-2"><LogOut size={16} /> Sign out</Button>
          </div>
        </div>

        <div className="flex gap-2 mt-8 border-b">
          {[["orders", "Orders", ClipboardList], ["enquiries", "My Enquiries", MessageSquare], ["profile", "My Details", User]].map(([id, label, Icon]) => (
            <button key={id} data-testid={`account-tab-${id}`} onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === id ? "border-accent text-steel" : "border-transparent text-muted-foreground hover:text-steel"}`}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {tab === "orders" && (
          <div className="mt-6 space-y-4" data-testid="account-orders">
            {orders.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Package className="mx-auto mb-3 text-slate-300" size={40} />
                No orders yet. <button onClick={() => navigate("/")} className="text-accent hover:underline">Start shopping →</button>
              </div>
            ) : orders.map((o) => (
              <div key={o.order_number} data-testid={`account-order-${o.order_number}`} className="rounded-xl border bg-white p-5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <span className="font-bold font-mono">{o.order_number}</span>
                    <span className="text-sm text-muted-foreground ml-3">{new Date(o.created_at).toLocaleDateString("en-NZ", { timeZone: "Pacific/Auckland" })}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className="bg-slate-600 hover:bg-slate-600 capitalize">{(o.status || "").replace(/_/g, " ")}</Badge>
                    <span className="font-bold font-mono text-accent">{money(o.total)}</span>
                  </div>
                </div>
                <div className="mt-3 text-sm text-muted-foreground">
                  {o.items.slice(0, 3).map((it) => `${it.qty}× ${it.name}`).join(", ")}{o.items.length > 3 ? "…" : ""}
                </div>
                {(o.payment?.paid_at || o.dispatched_at) && (
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    {o.payment?.paid_at && (
                      <span className="inline-flex items-center gap-1" data-testid={`order-paid-time-${o.order_number}`}>
                        <CreditCard size={12} className="text-emerald-600" /> Paid {fmtNZ(o.payment.paid_at)}
                      </span>
                    )}
                    {o.dispatched_at && (
                      <span className="inline-flex items-center gap-1" data-testid={`order-dispatched-time-${o.order_number}`}>
                        <Truck size={12} className="text-blue-600" /> Dispatched {fmtNZ(o.dispatched_at)}
                      </span>
                    )}
                  </div>
                )}
                <button onClick={() => navigate(`/order/${o.order_number}`)} className="mt-3 text-sm text-accent font-semibold hover:underline">View order & bank details →</button>
              </div>
            ))}
          </div>
        )}

        {tab === "enquiries" && (
          <div className="mt-6 space-y-4" data-testid="account-enquiries">
            {enquiries.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">No enquiries or quote requests yet.</div>
            ) : enquiries.map((e) => (
              <div key={e.id} className="rounded-xl border bg-white p-5">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{e.product_name || e.sku || "General enquiry"}</span>
                  <span className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleDateString("en-NZ", { timeZone: "Pacific/Auckland" })}</span>
                </div>
                {e.message && <p className="mt-2 text-sm text-muted-foreground">{e.message}</p>}
              </div>
            ))}
          </div>
        )}

        {tab === "profile" && (
          <div className="mt-6 max-w-xl rounded-xl border bg-white p-6" data-testid="account-profile">
            <h2 className="font-heading font-bold text-lg mb-4">Saved details for faster checkout</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Full name</Label><Input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></div>
              <div><Label>Phone</Label><Input data-testid="profile-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} /></div>
              <div className="sm:col-span-2"><Label>Company</Label><Input data-testid="profile-company" value={profile.company} onChange={(e) => setProfile({ ...profile, company: e.target.value })} /></div>
              <div className="sm:col-span-2"><Label>Address line 1</Label><Input data-testid="profile-line1" value={profile.saved_address.line1} onChange={(e) => setAddr("line1", e.target.value)} /></div>
              <div className="sm:col-span-2"><Label>Address line 2</Label><Input value={profile.saved_address.line2} onChange={(e) => setAddr("line2", e.target.value)} /></div>
              <div><Label>City</Label><Input data-testid="profile-city" value={profile.saved_address.city} onChange={(e) => setAddr("city", e.target.value)} /></div>
              <div><Label>Region</Label><Input value={profile.saved_address.region} onChange={(e) => setAddr("region", e.target.value)} /></div>
              <div><Label>Postcode</Label><Input value={profile.saved_address.postcode} onChange={(e) => setAddr("postcode", e.target.value)} /></div>
              <div><Label>Country</Label><Input value={profile.saved_address.country} onChange={(e) => setAddr("country", e.target.value)} /></div>
            </div>
            <Button data-testid="profile-save" onClick={saveProfile} disabled={saving} className="mt-5 bg-steel text-white hover:bg-steel-light font-semibold">
              {saving ? "Saving…" : "Save details"}
            </Button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
